// (B) 명령형 Modal(confirm/info/warning/error) 백엔드 — radix Dialog 기반 중앙 DialogStack.
// antd Modal.confirm 대체(D4-2). 싱글톤 store(realm 별 모듈 전역 — sonner 와 동일 격리: popup=별도 JS realm)에
// config 를 push 하고 KitThemeHost 에 마운트된 <DialogStackHost/> 가 렌더한다(React 트리 안 → context 상속).
//
// DOM 미러링:
//   base(.om-dialog-*/.om-dialog-*)는 PlainDialog 와 공유(omDialog.css, batch9 rename 유예).
//   confirm 전용 클래스는 .om-modal-confirm-*(DialogStack 이 유일 방출 → SCSS 를 동일 커밋에서 rename).
// 기본 OK/Cancel 텍스트는 ConfirmDefaultsContext(owma i18next → ThemeProvider 주입)에서 공급 — kit 은 i18n 미보유.
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  createContext,
  useContext,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react'
import type { OmwConfirmOptions, OmwModalHandle } from '../modal/useConfirm'
type OmConfirmOptions = OmwConfirmOptions
type OmModalHandle = OmwModalHandle
import { OmwButton, type OmwButtonProps } from '../api/OmwButton'
import { cx } from './cx'
import { acquireModalZIndex, releaseModalZIndex } from './modalZIndex'
import { useBodyScrollLock } from './a11y/bodyScrollLock'
import '../styles/omwModalConfirm.css'

export type ConfirmType = 'confirm' | 'info' | 'warning' | 'error'

// ── 기본 버튼 텍스트 context(owma i18next → ThemeProvider → KitThemeHost 주입). ──
export interface ConfirmDefaults {
  okText: string
  cancelText: string
}
export const ConfirmDefaultsContext = createContext<ConfirmDefaults>({
  okText: 'OK',
  cancelText: 'Cancel',
})

// ── 타입별 기본 아이콘(antd Modal.<type> 기본 = filled 아이콘, @ant-design/icons-svg path 인라인). ──
//    icon:null → 미표시, icon 지정 → 우선. confirm/warning 은 동일 느낌표(주황).
const ICON_VIEWBOX = '64 64 896 896'
const EXCLAMATION =
  'M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 010-96 48.01 48.01 0 010 96z'
const INFO =
  'M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm32 664c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V456c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272zm-32-344a48.01 48.01 0 010-96 48.01 48.01 0 010 96z'
const CLOSE_CIRCLE =
  'M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm127.98 274.82h-.04l-.08.06L512 466.75 384.14 338.88c-.04-.05-.06-.06-.08-.06a.12.12 0 00-.07 0c-.03 0-.05.01-.09.05l-45.02 45.02a.2.2 0 00-.05.09.12.12 0 000 .07v.02a.27.27 0 00.06.06L466.75 512 338.88 639.86c-.05.04-.06.06-.06.08a.12.12 0 000 .07c0 .03.01.05.05.09l45.02 45.02a.2.2 0 00.09.05.12.12 0 00.07 0c.02 0 .04-.01.08-.05L512 557.25l127.86 127.87c.04.04.06.05.08.05a.12.12 0 00.07 0c.03 0 .05-.01.09-.05l45.02-45.02a.2.2 0 00.05-.09.12.12 0 000-.07v-.02a.27.27 0 00-.05-.06L557.25 512l127.87-127.86c.04-.04.05-.06.05-.08a.12.12 0 000-.07c0-.03-.01-.05-.05-.09l-45.02-45.02a.2.2 0 00-.09-.05.12.12 0 00-.07 0z'

const TYPE_ICON: Record<ConfirmType, { d: string; color: string; name: string }> = {
  confirm: { d: EXCLAMATION, color: '#faad14', name: 'exclamation-circle' },
  warning: { d: EXCLAMATION, color: '#faad14', name: 'exclamation-circle' },
  info: { d: INFO, color: '#1677ff', name: 'info-circle' },
  error: { d: CLOSE_CIRCLE, color: '#ff4d4f', name: 'close-circle' },
}

// antd okCancel: confirm 만 OK+Cancel, info/warning/error 는 OK 단독.
const OK_CANCEL: Record<ConfirmType, boolean> = {
  confirm: true,
  info: false,
  warning: false,
  error: false,
}

function DefaultIcon({ type }: { type: ConfirmType }) {
  const spec = TYPE_ICON[type]
  return (
    <span
      role="img"
      aria-label={spec.name}
      className={`anticon anticon-${spec.name} om-modal-confirm-icon`}
      style={{ color: spec.color }}
    >
      <svg viewBox={ICON_VIEWBOX} width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d={spec.d} />
      </svg>
    </span>
  )
}

// ── 싱글톤 store(realm 별 모듈 전역) ──
interface StackEntry {
  id: number
  type: ConfirmType
  opts: OmConfirmOptions
  z: number
  // 열기 직전 포커스 요소(닫힘 시 수동 복원 — 명령형 소프트 포커스 관리).
  opener: HTMLElement | null
}
let entries: StackEntry[] = []
const listeners = new Set<() => void>()
let seq = 0

// z-index 자동 스태킹(antd 파리티): 열림 순서대로 상승. 선언형 OmDialog(PlainDialog)와 커서를 공유해
// (modalZIndex) 모달 종류와 무관하게 나중에 열린 것이 위로 뜬다. 명시 zIndex(예: export 모달 1900)는 그대로 사용.
function nextZ(bp: Record<string, unknown> | undefined): number {
  const explicit = typeof bp?.zIndex === 'number' ? (bp.zIndex as number) : undefined
  return acquireModalZIndex(explicit)
}

function notify(): void {
  for (const l of listeners) l()
}
function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => void listeners.delete(l)
}
function getSnapshot(): StackEntry[] {
  return entries
}

function updateEntry(id: number, next: OmConfirmOptions): void {
  entries = entries.map((e) =>
    e.id === id
      ? {
          ...e,
          opts: {
            ...e.opts,
            ...next,
            backendProps: { ...(e.opts.backendProps ?? {}), ...(next.backendProps ?? {}) },
          },
        }
      : e,
  )
  notify()
}

function closeEntry(id: number): void {
  const target = entries.find((e) => e.id === id)
  if (target == null) return
  entries = entries.filter((e) => e.id !== id)
  releaseModalZIndex()
  notify()
  // 닫힘 후 열기 직전 포커스 요소로 복원(여전히 문서에 있으면).
  if (target.opener != null && target.opener.isConnected) target.opener.focus()
  const afterClose = (target.opts.backendProps as Record<string, unknown> | undefined)?.afterClose
  if (typeof afterClose === 'function') (afterClose as () => void)()
}

export function openConfirm(type: ConfirmType, opts: OmConfirmOptions): OmModalHandle {
  const id = ++seq
  const z = nextZ(opts.backendProps as Record<string, unknown> | undefined)
  // 열기 직전 활성 요소를 저장 → closeEntry 에서 포커스 복원(명령형 소프트 포커스 관리).
  const opener = (document.activeElement as HTMLElement | null) ?? null
  entries = [...entries, { id, type, opts, z, opener }]
  notify()
  return {
    destroy: () => closeEntry(id),
    update: (next) => updateEntry(id, next),
  }
}

// antd Button config(escape hatch) → OmButton props 매핑.
const BTN_VARIANT: Record<string, OmwButtonProps['variant']> = {
  primary: 'primary',
  default: 'default',
  dashed: 'default',
  text: 'text',
  link: 'link',
}
const BTN_SIZE: Record<string, OmwButtonProps['size']> = {
  small: 'sm',
  middle: 'md',
  large: 'lg',
}

function toBtnProps(raw: unknown): Partial<OmwButtonProps> {
  if (raw == null || typeof raw !== 'object') return {}
  const p = raw as Record<string, unknown>
  const out: Partial<OmwButtonProps> = {}
  if (typeof p.className === 'string') out.className = p.className
  if (p.style != null) out.style = p.style as CSSProperties
  if (typeof p.disabled === 'boolean') out.disabled = p.disabled
  if (typeof p.loading === 'boolean') out.loading = p.loading
  if (typeof p.type === 'string' && BTN_VARIANT[p.type] != null) out.variant = BTN_VARIANT[p.type]
  if (typeof p.size === 'string' && BTN_SIZE[p.size] != null) out.size = BTN_SIZE[p.size]
  return out
}

type FooterFn = (originNode: ReactNode, extra: { OkBtn: FC; CancelBtn: FC }) => ReactNode

function ConfirmDialog({ entry }: { entry: StackEntry }) {
  const { id, type, opts, z } = entry
  const defaults = useContext(ConfirmDefaultsContext)
  const [loading, setLoading] = useState(false)
  const bp = (opts.backendProps ?? {}) as Record<string, unknown>

  // ⚠ confirmBase(utils/ui) 계열은 title/content/okText/cancelText/onOk/onCancel 을 전부 backendProps 안에
  //   담아 넘긴다(구 antd toAntConfig 가 backendProps 를 spread 했기 때문). 직접 호출처는 top-level 사용.
  //   → 구 toAntConfig 처럼 top-level opts.X 를 우선하고 없으면 backendProps.X 로 폴백해 양쪽을 모두 지원한다.
  const title = opts.title ?? (bp.title as ReactNode | undefined)
  const content = opts.content ?? (bp.content as ReactNode | undefined)
  const onOk =
    opts.onOk ?? (typeof bp.onOk === 'function' ? (bp.onOk as OmConfirmOptions['onOk']) : undefined)
  const onCancel =
    opts.onCancel ??
    (typeof bp.onCancel === 'function' ? (bp.onCancel as OmConfirmOptions['onCancel']) : undefined)

  const okText = opts.okText ?? (typeof bp.okText === 'string' ? bp.okText : defaults.okText)
  const cancelText =
    opts.cancelText ?? (typeof bp.cancelText === 'string' ? bp.cancelText : defaults.cancelText)
  const showCancel = OK_CANCEL[type]
  const closable = bp.closable === true // antd Modal.confirm 기본 closable=false
  const maskClosable = bp.maskClosable === true // antd Modal.confirm 기본 maskClosable=false
  const keyboard = bp.keyboard !== false // antd 기본 keyboard=true(Esc 닫힘)
  const centered = bp.centered === true
  const width = typeof bp.width === 'number' ? bp.width : 416 // antd Modal.confirm 기본 폭
  const zIndex = z
  const wrapClassName = typeof bp.wrapClassName === 'string' ? bp.wrapClassName : undefined
  const boxClassName = typeof bp.className === 'string' ? bp.className : undefined
  const getContainer = typeof bp.getContainer === 'function' ? bp.getContainer : undefined
  const container =
    getContainer != null ? ((getContainer as () => HTMLElement | null)() ?? undefined) : undefined
  // 배경 스크롤 락(커스텀 컨테이너로 열리면 생략).
  useBodyScrollLock(container == null)

  const close = (): void => closeEntry(id)
  const handleCancel = (): void => {
    onCancel?.()
    close()
  }
  const isThenable = (v: unknown): v is Promise<unknown> =>
    v != null && typeof (v as Promise<unknown>).then === 'function'

  const handleOk = (): void => {
    if (onOk == null) {
      close()
      return
    }
    // antd ActionButton 파리티: onOk 가 인자(close)를 선언하면(arity≥1) close 를 넘기고 자동 닫기하지 않는다
    //   (onOk 가 직접 close() 호출로 닫음). 인자가 없으면 실행 후 프로미스가 아니면 자동 닫기.
    //   프로미스 반환 시엔 loading 후 resolve 에서 닫고 reject 면 유지.
    const okFn = onOk as (close?: () => void) => unknown
    let result: unknown
    if (okFn.length >= 1) {
      result = okFn(close)
    } else {
      result = okFn()
      if (!isThenable(result)) {
        close()
        return
      }
    }
    if (isThenable(result)) {
      setLoading(true)
      result.then(
        () => {
          setLoading(false)
          close()
        },
        () => setLoading(false), // reject → 모달 유지(antd 파리티)
      )
    }
  }

  const hasTitle = title != null
  const iconNode: ReactNode =
    bp.icon === null ? null : bp.icon !== undefined ? (
      (bp.icon as ReactNode)
    ) : (
      <DefaultIcon type={type} />
    )

  const okBtnProps = toBtnProps(bp.okButtonProps)
  const cancelBtnProps = toBtnProps(bp.cancelButtonProps)
  const okBtnEl = (
    <OmwButton
      variant="primary"
      {...okBtnProps}
      loading={loading || okBtnProps.loading}
      onClick={handleOk}
    >
      {okText}
    </OmwButton>
  )
  const cancelBtnEl = showCancel ? (
    <OmwButton variant="default" {...cancelBtnProps} onClick={handleCancel}>
      {cancelText}
    </OmwButton>
  ) : null
  const defaultBtns = (
    <>
      {cancelBtnEl}
      {okBtnEl}
    </>
  )

  // antd Modal v5 파리티: footer 렌더프롭 extra 의 OkBtn/CancelBtn 은 "컴포넌트"(FC)로 넘긴다 →
  //   소비처가 <OkBtn /> / <CancelBtn /> 로 렌더한다. element(객체)로 넘기면 <object/> 가 되어
  //   "Element type is invalid ... got: object" 로 크래시(SecureSend·서명관리·일정편집 등 13+ 소비처).
  const OkBtn: FC = () => okBtnEl
  const CancelBtn: FC = () => cancelBtnEl

  let footerNode: ReactNode
  const footer = bp.footer
  if (footer === null) footerNode = null
  else if (typeof footer === 'function')
    footerNode = (footer as FooterFn)(defaultBtns, { OkBtn, CancelBtn })
  else if (footer !== undefined) footerNode = footer as ReactNode
  else footerNode = defaultBtns

  const closeIcon = bp.closeIcon as ReactNode

  return (
    <DialogPrimitive.Root
      open
      modal={false}
      onOpenChange={(next) => {
        if (!next && keyboard) handleCancel()
      }}
    >
      <DialogPrimitive.Portal container={container}>
        {/* ⚠ Radix Overlay 는 modal={false} 에서 null 을 렌더한다 → antd Modal.confirm 의 배경 마스크(dimming)가
            사라짐. 플레인 div 로 마스크를 직접 렌더한다(닫힘은 wrap onClick 이 담당, 마스크는 순수 시각). */}
        <div className="om-dialog-mask" aria-hidden="true" style={{ zIndex }} />
        <div
          className={cx('om-dialog-wrap', centered && 'om-dialog-centered', wrapClassName)}
          style={zIndex != null ? { zIndex } : undefined}
          onClick={(e) => {
            if (e.target === e.currentTarget && maskClosable) handleCancel()
          }}
        >
          <DialogPrimitive.Content
            className={cx(
              'om-dialog',
              'om-modal-confirm',
              `om-modal-confirm-${type}`,
              boxClassName,
            )}
            style={width != null ? { width } : undefined}
            aria-describedby={undefined}
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => {
              if (!keyboard) e.preventDefault()
            }}
            // 닫힘 포커스 복원은 closeEntry(opener)가 담당 → Radix 기본 복원과 충돌 방지.
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="om-dialog-content">
              {closable && (
                <DialogPrimitive.Close
                  className="om-dialog-close"
                  aria-label="Close"
                  onClick={handleCancel}
                >
                  <span className="om-dialog-close-x">
                    {closeIcon ?? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 3l10 10M13 3L3 13"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </span>
                </DialogPrimitive.Close>
              )}
              {/* antd Modal.confirm 파리티: .ant-modal-content > .ant-modal-body > .ant-modal-confirm-body-wrapper.
                  om-dialog-body(=.ant-modal-body)를 빠뜨리면 소비처 SCSS(.om-popup-base … .om-dialog-body{padding})가
                  매칭되지 않아 예약발송·사용자선택 등 om-popup-base 팝업의 내부 padding/gap 이 사라진다. */}
              <div className="om-dialog-body">
                <div className="om-modal-confirm-body-wrapper">
                  <div
                    className={cx(
                      'om-modal-confirm-body',
                      hasTitle && 'om-modal-confirm-body-has-title',
                    )}
                  >
                    {iconNode}
                    <div className="om-modal-confirm-paragraph">
                      {hasTitle ? (
                        <DialogPrimitive.Title className="om-modal-confirm-title">
                          {title}
                        </DialogPrimitive.Title>
                      ) : (
                        <DialogPrimitive.Title className="om-dialog-sr-only">
                          {' '}
                        </DialogPrimitive.Title>
                      )}
                      <div className="om-modal-confirm-content">{content}</div>
                    </div>
                  </div>
                  {footerNode != null && <div className="om-modal-confirm-btns">{footerNode}</div>}
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

// KitThemeHost 에 마운트 — store 를 구독해 열린 confirm 다이얼로그 스택을 렌더한다.
export function DialogStackHost() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return (
    <>
      {list.map((e) => (
        <ConfirmDialog key={e.id} entry={e} />
      ))}
    </>
  )
}
